import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography, RadioGroup, FormControlLabel, Radio } from '@mui/material';
import CustomModal from '../../../../../shared/CustomModal/CustomModal';
import CustomCard from '../../../../../shared/CustomCard/CustomCard';
import CustomButton from '../../../../../shared/CustomButton/CustomButton';
import zeplinColor from '../../../../../../theme/Color';
import { useAppSelector } from '../../../../../../store/hooks';

interface ChangeRoleModalProps {
  open: boolean;
  onConfirm: (role: string) => void;
  onCancel: () => void;
  loading?: boolean;
}

const ChangeRoleModal: React.FC<ChangeRoleModalProps> = ({
  open,
  onConfirm,
  onCancel,
  loading = false,
}) => {
  const roles = useAppSelector((state) => state.rolesAndPermissions.filterRoles);

  const [selectedRole, setSelectedRole] = useState<string>(roles[0]?.roleId ?? '');

  const handleRoleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedRole(event.target.value);
  };

  const handleConfirm = () => {
    onConfirm(selectedRole);
  };

  return (
    <CustomModal handleClose={onCancel} open={open}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
        }}
      >
        <CustomCard
          styles={{
            borderRadius: '14px',
            padding: { xs: '20px', xl: '24px' },
            backgroundColor: zeplinColor.Neutral1,
            // width: '375px',
            boxShadow: 'rgba(0, 0, 0, 0.15)',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <Typography variant='responsiveHeading3' color={zeplinColor.BasicBlack}>
              Change user role
            </Typography>

            <Typography variant='responsiveBody3' color={zeplinColor.Neutral8}>
              Choose a new role to apply to selected users.
            </Typography>

            <RadioGroup value={selectedRole} onChange={handleRoleChange}>
              {roles.map((role) => (
                <FormControlLabel
                  key={role.roleId}
                  value={role.roleId}
                  control={
                    <Radio
                      sx={{
                        color: zeplinColor.Neutral5,
                        '&.Mui-checked': {
                          color: zeplinColor.BasicBlack,
                        },
                      }}
                    />
                  }
                  label={
                    <Typography variant='responsiveBody3' color={zeplinColor.BasicBlack}>
                      {role.roleName}
                    </Typography>
                  }
                />
              ))}
            </RadioGroup>

            <Box
              sx={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '8px' }}
            >
              <CustomButton
                disabled={false}
                onClick={onCancel}
                variant='outlined'
                color='primary'
                style={{
                  borderRadius: '8px',
                  height: '40px',
                  padding: '8px 16px',
                  flex: 1,
                  border: `1px solid ${zeplinColor.BasicBlack}`,
                  '&.hover': {
                    backgroundColor: zeplinColor.Neutral3,
                  },
                }}
                textStyle={{ color: zeplinColor.BasicBlack }}
                text='Cancel'
              />
              <CustomButton
                onClick={handleConfirm}
                variant='contained'
                color='primary'
                style={{
                  borderRadius: '8px',
                  height: '40px',
                  padding: '8px 16px',
                  backgroundColor: loading ? zeplinColor.Neutral5 : zeplinColor.BasicBlack,
                  flex: 1,
                }}
                textStyle={{ color: loading ? zeplinColor.Neutral8 : zeplinColor.Neutral1 }}
                text={loading ? 'Saving...' : 'Save'}
                disabled={loading}
              />
            </Box>
          </Box>
        </CustomCard>
      </Box>
    </CustomModal>
  );
};

export default ChangeRoleModal;
